<script lang="ts" setup>
import useNotifications from '~/stores/notifications';
import { useDebounceFn } from '@vueuse/core';

const { query, mutation, data } = useDeliveryNote();
const { send } = useNotifications();
const { t } = useI18n();
const deliveryNote = ref('');
const isSaving = ref(false);
const hasChanges = ref(false);
const showSuccessIcon = ref(false);

watch(
  data,
  (val) => {
    deliveryNote.value = val || '';
  },
  { immediate: true }
);

watch(deliveryNote, () => {
  hasChanges.value = deliveryNote.value !== data.value;
});

const debouncedSave = useDebounceFn(() => {
  if (hasChanges.value) {
    saveNote();
  }
}, 800);

watch(deliveryNote, () => {
  if (hasChanges.value) {
    debouncedSave();
  }
});

const saveNote = () => {
  if (!hasChanges.value) return;
  
  isSaving.value = true;
  mutation.mutate(deliveryNote.value, {
    onSuccess: () => {
      isSaving.value = false;
      hasChanges.value = false;
      showSuccessIcon.value = true;
      send({
        message: t('Delivery notes saved'),
        type: 'success',
      });

      setTimeout(() => {
        showSuccessIcon.value = false;
      }, 2000);
    },
    onError: () => {
      isSaving.value = false;
      send({
        message: t('Failed to save delivery notes'),
        type: 'error',
      });
    },
  });
};
</script>

<template>
  <div class="flex flex-col gap-3">
    <div class="flex items-start gap-3">
      <div class="mt-1 text-gray-500">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 0 1 .865-.501 48.172 48.172 0 0 0 3.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0 0 12 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018Z" />
        </svg>
      </div>
      <div class="flex-1">
        <p class="text-gray-700 mb-2">{{ t('Add special instructions for your delivery') }}</p>
        
        <div v-if="!query.isLoading" class="relative">
          <FormKit
            v-model="deliveryNote"
            type="textarea"
            rows="3"
            :placeholder="t('E.g., Leave package at the back door, Call before delivery...')"
            class="w-full transition-all focus:border-primary focus:ring-primary"
          />
          
          <div class="absolute right-2 top-2 flex items-center gap-1">
            <span v-if="isSaving" class="text-gray-400 text-xs flex items-center">
              <span class="loading loading-spinner loading-xs mr-1"></span>
              {{ t('Saving...') }}
            </span>
            <transition name="fade">
              <span v-if="showSuccessIcon" class="text-green-500">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                </svg>
              </span>
            </transition>
          </div>
          
          <div v-if="hasChanges" class="mt-3 flex justify-end">
            <VButton 
              @click="saveNote" 
              class="w-fit" 
              size="sm"
              variant="secondary"
              :disabled="isSaving">
              {{ t('Save') }}
            </VButton>
          </div>
        </div>
        
        <div v-else class="h-20 flex items-center justify-center">
          <span class="loading loading-spinner loading-md"></span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
